<template>
  <div class="background">
    <div class="container">

      <div v-if="!pageIsLoaded" class="spinner-container">
        <spinner-el class="spinner" />
      </div>

      <div class="currency" v-else>

        <table class="table-top">
          <tr class="table-top__tr">
            <td class="table-top__td table-top__td_currency-selector">
              <currency-selector @input="moveToCurrencyByTickerId(selectedCurrency.id)" v-model="selectedCurrency" long/>
            </td>
            <td class="table-top__td table-top__td_homepage">
              <div class="table-top__label">Homepage</div>
              <a
                v-if="selectedCurrency.homepageUrl"
                :href="selectedCurrency.homepageUrl"
                class="table-top__homepage"
                target="_blank">{{selectedCurrency.homepageUrl}}</a>
            </td>
          </tr>
        </table>


        <div class="market-overview">
          <head-title class="head-title">
            Market Overview
          </head-title>
          <table class="market-overview__table">
            <tr class="market-overview__tr">
              <td class="market-overview__td market-overview__td_price">
                <div class="market-overview__label">Price</div>
                <div class="market-overview__data-container">
                  <div class="market-overview__value">{{selectedCurrency | getObjPropInSelectedCurrency('price', currencyCode) | formatMoney(currencyCode)}}</div>
                  <div class="market-overview__chart-container">
                    <div class="market-overview__difference">
                      <percent-trend :value="selectedCurrency | getObjPropInSelectedCurrency('percentChange24h', currencyCode)"></percent-trend>
                    </div>
                    <graph-el :tickerId="selectedCurrency.id" class="chart"/>
                  </div>
                </div>
              </td>
              <td class="market-overview__td market-overview__td_marketcap">
                <div class="market-overview__label">Market cap</div>
                <div class="market-overview__data-container">
                  <div class="market-overview__value">{{selectedCurrency | getObjPropInSelectedCurrency('marketCap', currencyCode) | formatMoney(currencyCode)}}</div>
                  <div class="market-overview__chart-container">
                    <div class="market-overview__difference">
                      <percent-trend :value="selectedCurrency | getObjPropInSelectedCurrency('marketCapPercentChange24h', currencyCode)"></percent-trend>
                    </div>
                    <graph-el :src="`/images/ticker/sparkline/market_cap/${currencyCode}/${selectedCurrency.id}/preview.svg`" class="chart"/>
                  </div>
                </div>
              </td>
              <td class="market-overview__td market-overview__td_volume">
                <div class="market-overview__label">24h volume</div>
                <div class="market-overview__data-container">
                  <div class="market-overview__value">{{selectedCurrency | getObjPropInSelectedCurrency('marketVol24h', currencyCode) | formatMoney(currencyCode)}}</div>
                  <div class="market-overview__chart-container">
                    <graph-el :src="`/images/ticker/sparkline/market_vol24h/${currencyCode}/${selectedCurrency.id}/preview.svg`" class="chart"/>
                  </div>
                </div>
              </td>
            </tr>
          </table>
        </div>
        <chart-block
          v-if="availableDateRanges.length && selectedCurrency"
          :selectedCurrency="selectedCurrency"
          :availableDateRanges="availableDateRanges"
        />
        <div class="assigned-alerts" v-if="loginInfo">
          <div class="assigned-alerts__title">
            <head-title class="head-title">Assigned alerts</head-title>
            <router-link class="assigned-alerts__btn-container" :to="`/add-alert/${selectedCurrency.id}`">
              <button-el red class="assigned-alerts__create-alert">
                CREATE ALERT
              </button-el>
            </router-link>
          </div>
          <table-el gray class="table-alerts">
            <tr slot="thead" class="table-alerts__thead">
              <th class="table-alerts__th table-alerts__th_notification-text">NOTIFICATION TEXT</th>
              <th class="table-alerts__th table-alerts__th_date">creation date</th>
              <th class="table-alerts__th table-alerts__th_status">status</th>
              <th class="table-alerts__th table-alerts__th_link"></th>
            </tr>
            <tr
              class="table-alerts__tr"
              slot="tbody"
              v-if="assignedAlerts && assignedAlerts.length"
              v-for="alert in showedAlerts"
              :key="alert.alertID"
            >
              <td class="table-alerts__td table-alerts__td_notification-text" :title="alert.message">{{alert.message}}</td>
              <td
                class="table-alerts__td table-alerts__td_date"
                :title="alert.createdAt | format(dateTimeFormat)"
              >{{alert.createdAt | formatDate(shortMonthDateFormat)}}</td>
              <td class="table-alerts__td table-alerts__td_status">{{alert.status}}</td>
              <td class="table-alerts__td table-alerts__td_link">
                <router-link :to="moveToAlertCreation(alert.alertID, alert.status)" class="table-alerts__icon">
                  <icon-base red>
                    <icon-arrow-btn/>
                  </icon-base>
                </router-link>
              </td>
            </tr>
          </table-el>
          <div class="assigned-alerts__notice" v-if="assignedAlerts && assignedAlerts.length">
            You can <span v-if="alertsMoreThanPermissibleQuantity"><red-text class="load-all" @click.native="loadAllAlerts">load all</red-text> alerts for this currency or</span>&nbsp;<router-link to="/alert-management">manage alerts</router-link> not related to&nbsp;this currency...
          </div>
          <div v-else class="assigned-alerts__empty-case">
            There are no entries yet...
          </div>
        </div>

      </div>

    </div>
  </div>
</template>

<script>
import { format } from 'date-fns';
import { mapGetters, mapState } from 'vuex';
import { shortMonthDateFormat, dateTimeFormat } from 'src/utils/config';
import { formatMoney, formatDate, getObjPropInSelectedCurrency } from 'src/utils/helpers';
import { IconArrowBtn } from 'Icons';
import ChartBlock from './chartBlock';

export const maxCountRowsVisibleInAssignedAlerts = 5;
export default {
  data: () => ({
    alertsMoreThanPermissibleQuantity: false,
    pageIsLoaded: false,
    assignedAlerts: null,
    selectedCurrency: null,
    availableDataPeriods: null,
    dateRanges: [
        { name: '1d', value: '1 day' },
        { name: '7d', value: '1 week' },
        { name: '1m', value: '1 month' },
        { name: '3m', value: '3 months' },
        { name: '1y', value: '1 year' },
        { name: 'YTD', value: 'Year to date' },
        { name: 'ALL', value: 'All' },
    ],
  }),
  props: {
    tickerId: {
      type: Number,
    },
  },
  components: {
    IconArrowBtn,
    ChartBlock,
  },
  filters: {
    formatMoney,
    formatDate,
    format,
    getObjPropInSelectedCurrency,
  },
  computed: {
    ...mapState('userData', ['loginInfo']),
    ...mapGetters('userData', ['currencyCode']),
    shortMonthDateFormat: () => shortMonthDateFormat,
    dateTimeFormat: () => dateTimeFormat,
    showedAlerts() {
      return this.alertsMoreThanPermissibleQuantity ? this.assignedAlerts.slice(0, maxCountRowsVisibleInAssignedAlerts) : this.assignedAlerts;// eslint-disable-line max-len
    },
    availableDateRanges() {
      return this.dateRanges.filter(
        r => this.availableDataPeriods && this.availableDataPeriods.includes(r.name),
      );
    },
  },
  methods: {
    moveToAlertCreation(alertID, alertStatus) {
      if (alertStatus === 'archived') {
        return `/repeat-alert/${alertID}`;
      }
      return `/edit-alert/${alertID}`;
    },
    moveToCurrencyByTickerId(tickerId) {
      this.$router.replace(`/currency/${tickerId}`);
    },
    getCurrency(tickerId) {
      const requests = [
        this.$apiService.getTickerByTickerId(tickerId).then((currencyData) => {
          this.selectedCurrency = currencyData;
        }),
      ];
      if (this.loginInfo) {
        requests.push(this.getAlertsByTicker(tickerId));
      }
      return Promise.all(requests);
    },
    getAlertsByTicker(tickerID) {
      return this.$apiService.getAlertsByTicker(tickerID).then((alertData) => {
        this.assignedAlerts = alertData || [];
        this.alertsMoreThanPermissibleQuantity = maxCountRowsVisibleInAssignedAlerts < this.assignedAlerts.length;// eslint-disable-line max-len
      });
    },
    loadAllAlerts() {
      this.alertsMoreThanPermissibleQuantity = false;
    },
    getTickerAvailablePeriods() {
      this.$apiService.getTickerAvailablePeriods(this.tickerId).then((data) => {
        this.availableDataPeriods = data;
      });
    },
  },
  watch: {
    tickerId() {
      this.getCurrency(this.tickerId);
      this.getTickerAvailablePeriods();
      if (this.loginInfo) {
        this.getAlertsByTicker(this.tickerId);
      }
    },
    loginInfo(val) {
      if (val) {
        this.getAlertsByTicker(this.tickerId);
      }
    },
  },
  created() {
    Promise.all([
      this.getCurrency(this.tickerId),
      this.getTickerAvailablePeriods(),
    ]).finally(() => {
      this.pageIsLoaded = true;
    });
  },
};
</script>

<style lang="scss" scoped>
  $side-padding: 10px;
  .background {
    @include background;
    padding: 30px $side-padding 60px;
  }
  .container {
    @include container;
  }

  .table-top {
    width: 100%;
    border-radius: 5px;
    border-spacing: 0;
    margin-bottom: 31px;
    &__td {
      padding: 30px 34px 28px;
      &:last-child {
        border-right: none;
      }
      &_currency-selector {
        width: 30%;
        padding-left: 0;
      }
      &_homepage {
        padding-left: 67px;
      }
    }
    &__label {
      @include font(12px, $grey-color1, 500);
      margin-bottom: 10px;
    }
    &__date {
      @include font(13px, $black-color1);
    }
    &__homepage {
      @include font(13px, $red-color3 !important, 500);
      text-decoration: none;
      @include trimOverflowText(200px);
      display: block;
    }
    &__label,
    &__homepage {
      line-height: 15px;
    }
  }
  .assigned-alerts {
    background: $white-color2;
    border-radius: 5px;
    &__notice {
      line-height: 16px;
      padding: 24px 30px;
      @include font(13px, $grey-color9, 500);
      a {
        color: $red-color3 !important;
        text-decoration: none;
      }
    }
    &__empty-case {
      padding: 24px 30px;
      display: flex;
      align-items: center;
      @include font(13px, $grey-color9, 500);
    }
    &__title {
      padding: 32px 31px;
    }
    .head-title {
      display: inline-block;
    }
    &__btn-container {
      margin-left: 59px;
      display: inline-block;
      width: 148px;
    }
  }
  .table-alerts {
    width: 100%;
    &__th {
      text-align: left;
      $padding: 30px;
      padding-left: $padding;
      padding-right: $padding;
    }
    &__td {
      border-bottom: 1px solid $grey-color10;
      padding: 24px 30px;
      &_link {
        padding: 0 0 0 24px;
      }
      &_status {
        text-transform: capitalize;
      }
    }
    &__td,
    &__th {
      &_notification-text {
        line-height: 16px;
      }
      &_date {
        width: 204px;
      }
      &_status {
        width: 126px;
        padding-left: 37px;
        padding-right: 34px;
      }
      &_link {
        width: 100px;
      }
    }
    &__icon {
      margin-left: 25px;
    }
  }

  .market-overview {
    background: $white-color2;
    margin-bottom: 49px;
    border-radius: 5px;
    .head-title {
      padding: 32px 31px 31px;
    }
    &__data-container {
      display: flex;
    }
    &__chart-container {
      display: flex;
      align-items: center;
      margin-left: 6px;
    }
    &__table {
      width: 100%;
      border-top: 1px solid $grey-color3;
      border-collapse: collapse;
    }
    &__td {
      width: 33%;
      padding: 38px 30px 36px;
      border-right: 1px solid $grey-color3;
      &_price {
        min-width: 326px;
      }
      &_marketcap {
        min-width: 385px;
      }
      &:last-child {
        border-right: none;
      }
    }
    &__value {
      @include font(18px, $black-color1, 500);
    }
    &__difference {
      font-size: 14px;
      .value {
        @include valueLabel;
        &_red {
          &:before {
            background: $red-color3;
          }
        }
        &_green {
          &:before {
            background: $green-color1;
          }
        }
      }
    }
    &__label {
      @include font(12px, $grey-color1, 500);
      margin-bottom: 12px;
    }
  }
  .chart {
    margin-left: 30px;
    height: 16px;
    width: 60px;
    object-fit: cover;
    @include clearfix;
  }
  .cur-selector {
    width: 330px;
  }
  .spinner-container {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: -60px;
  }
  .load-all {
    display: inline-block;
    cursor: pointer;
  }
  @media screen and (max-width: 1080px) {
    .market-overview {
      &__data-container {
        flex-direction: column;
      }
      &__chart-container {
        flex-direction: row-reverse;
        justify-content: flex-end;
        margin-left: 0;
        margin-top: 16px;
      }
      &__difference {
        margin-right: 0;
        margin-left: 15px;
      }
      &__label {
        margin-bottom: 18px;
      }
      &__td {
        &_price {
          min-width: auto;
        }
        &_marketcap {
          min-width: auto;
        }
      }
    }
    .chart {
      margin-left: 0px;
      margin-right: 15px;
    }
  }

  @media screen and (max-width: 816px) {
    .assigned-alerts__btn-container {
      display: none;
    }
    .table-alerts {
      &__th,
      &__td {
        &_date {
          width: 156px;
        }
        &_link {
          display: none !important;
        }
      }
    }
    .table-top__td_homepage {
      padding-left: 0;
    }
  }
  @media screen and (max-width: 762px) {
    $column-padding: 18px;
    .market-overview {
      &__tr {
        display: flex;
        flex-direction: column;
      }
      &__td {
        width: 100%;
        border-right: none;
        padding-bottom: 30px - $column-padding;
        &:not(:first-child) {
          padding-top: 30px;
        }
        &:not(:last-child) {
          border-bottom: $common-border;
        }
        &:first-child {
          padding-top: 0;
          margin-top: -3px;
        }
      }
      &__table {
        border-top: none;
      }
      &__data-container {
        flex-direction: row;
        flex-wrap: wrap;
      }
      &__difference {
        margin-left: 0;
        margin-right: 15px;
      }
      &__value {
        margin-right: 30px;
      }
      &__chart-container {
        flex-direction: row;
        justify-content: flex-start;
        margin-top: 0;
      }
      &__value,
      &__chart-container {
        margin-bottom: $column-padding;
      }
    }
  }
  @media screen and (max-width: 638px) {
    .background {
      padding-top: 26px;
    }
    .table-top {
      margin-bottom: $side-padding * 2;
      &__tr {
        display: flex;
        flex-direction: column-reverse;
      }
      &__td {
        &_homepage {
          padding: 0 0 0 20px;
          display: flex;
          align-items: flex-end;
        }
        &_currency-selector {
          padding: 21px 0 0;
        }
      }
      &__label {
        margin-bottom: 0;
      }
      &__homepage {
        margin-left: 9px;
      }
    }
    .market-overview {
      &__td,
      .head-title {
        $padding: 20px;
        padding-right: $padding;
        padding-left: $padding;
      }
    }
  }
  @media screen and (max-width: 580px) {
    .assigned-alerts__notice {
      padding: 11px 22px 17px;
    }
    .assigned-alerts__title {
      padding-bottom: 10px;
    }
    .table-alerts {
      &__thead {
        display: none;
      }
      &__tr {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        padding-top: 16px;
      }
      &__td {
        display: flex;
        &_notification-text {
          order: 2;
          width: 100%;
          padding-top: 11px;
          padding-bottom: 21px;
        }
        &_status {
          order: 1;
        }
        &_date {
          order: 0;
        }
        &_status,
        &_date {
          border-bottom: none;
          color: $grey-color1 !important;
          padding-bottom: 0;
          padding-top: 0;
          width: auto;
        }
        &_notification-text,
        &_status,
        &_date {
          $padding-side: 28px;
          padding-left: $padding-side;
          padding-right: $padding-side;
        }
      }
    }
    .chart-container {
      width: calc(100% + #{$side-padding * 2});
      margin-left: -$side-padding;
      border-radius: 0;
      @include incrSelectorWeight(1) {
        margin-bottom: $side-padding * 2;
      }
    }
    .market-overview {
      margin-bottom: $side-padding * 2;
    }
  }
  @media screen and (max-width: 446px) {
    .market-overview {
      &__data-container {
        flex-direction: column;
      }
      &__chart-container {
        flex-direction: row-reverse;
        justify-content: flex-end;
      }
    }
  }
  @media screen and (max-width: 363px) {
    .table-top__td_currency-selector,
    .cur-selector {
      width: 100%;
      max-width: calc(100vw - #{$side-padding * 2});
    }
    .table-top__homepage {
      max-width: 170px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/currency/index.vue